import { isWeb } from "@memorang/helpers/platform";
import { View } from "react-native";
import { Appbar, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import AppbarAction from "../AppbarAction";
import Box from "../Box";
import Button from "../Button";
import { CustomBreadcrumb } from "../CustomBreadcrumb";
import CommandbarContainer from "./CommandbarContainer";
import type { CustomAppBarProps } from "./types";

const RightContent = (
	props: Pick<
		CustomAppBarProps,
		| "hideCommandBar"
		| "examSwitcher"
		| "renderRight"
		| "renderCustomCommandBar"
		| "handleTrackEvent"
	>,
) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();

	if (props.renderRight) {
		return props.renderRight();
	}

	const handleTrackEvent =
		props.handleTrackEvent ??
		(() => {
			return;
		});

	const showCommandBar = !props.hideCommandBar && isWeb && !isMobile;

	return (
		<Box flexDirection="row" alignItems="center" gap={8}>
			{props.renderCustomCommandBar ? (
				props.renderCustomCommandBar()
			) : showCommandBar ? (
				<CommandbarContainer handleTrackEvent={handleTrackEvent} />
			) : null}
			{props.examSwitcher && !!props.examSwitcher.label ? (
				<Button
					mode="outlined"
					asset="endIcon"
					icon="chevron-down"
					style={{
						borderRadius: theme.roundness * 99,
						borderColor: theme.colors.surfaceVariant,
					}}
					labelStyle={{ color: theme.colors.onSurface }}
					onPress={props.examSwitcher.onPress}
				>
					{props.examSwitcher.label}
				</Button>
			) : null}
		</Box>
	);
};

export const CustomAppBar = ({
	title,
	centerTitle,
	breadcrumbs,
	navigation,
	onPressBack,
	backDisabled,
	hideMenu,
	renderLeft,
	...props
}: CustomAppBarProps) => {
	const theme = useAppTheme();
	const { isMobile, isMobileWeb } = useDevice();

	const showHamburgerMenu =
		!hideMenu && !onPressBack && isMobileWeb && !!navigation?.toggleDrawer;
	const showBreadcrumbs = !!breadcrumbs?.length && isWeb && !isMobile;
	const shouldCenterTitle = centerTitle && !showBreadcrumbs;
	const titleContent = showBreadcrumbs ? (
		<CustomBreadcrumb breadcrumbs={breadcrumbs} />
	) : typeof title === "string" ? (
		<Text numberOfLines={1} variant="titleMedium" testID="app-bar-title">
			{title}
		</Text>
	) : (
		title
	);

	return (
		<Box zIndex={1}>
			<Appbar.Header
				theme={theme}
				mode="small"
				style={{
					backgroundColor: theme.colors.surface,
					paddingRight: 16,
					height: 64,
				}}
				elevated
			>
				{showHamburgerMenu && (
					<AppbarAction
						icon="menu"
						accessibilityRole="button"
						accessibilityLabel="Open menu"
						onPress={() => navigation?.toggleDrawer?.()}
					/>
				)}
				{onPressBack ? (
					<AppbarAction
						icon="arrow-left"
						accessibilityRole="button"
						accessibilityLabel="Go back"
						onPress={onPressBack}
						disabled={backDisabled}
					/>
				) : null}
				{renderLeft?.()}
				<Appbar.Content title={shouldCenterTitle ? null : titleContent} />
				{shouldCenterTitle ? (
					<View
						style={{
							position: "absolute",
							inset: 0,
							justifyContent: "center",
							alignItems: "center",
							pointerEvents: "none",
						}}
					>
						{titleContent}
					</View>
				) : null}
				<RightContent {...props} />
			</Appbar.Header>
		</Box>
	);
};

export default CustomAppBar;
